import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react'
import CommonModal from '../../../../shared/components/modal/common-modal'
import { useTranslation } from 'react-i18next'
import CommonTable from '../../../../shared/components/common/table'
import { Flex, notification, Row, Space, Spin, Typography } from 'antd'
import saveAs from 'file-saver'
import './index.scss'
import { DownloadIcon } from '../../../../../assets/icons'
import { colorBgError, colorBgErrorBold, colorBorder } from '../../../../themes/primaryThemeDefault'
import { StatusTag } from './StatusTag'
import { LoadingOutlined } from '@ant-design/icons'
import { SYSTEM_MESSAGE_IDS } from 'src/app/shared/constants/systemMessages.constants'
import { TableColumnDefs } from 'src/app/shared/components/common/new-table/table.model'
import { getErrorMessage } from 'src/app/features/feature-client-setup/account-setup/account-setup-upload/useAccountSetupUpload'

interface ErrorDetailsUploadProps {
  onClose?: () => any
  ref?: any
  errorRows?: any
}

const ErrorDetailsUpload: React.FC<ErrorDetailsUploadProps> = forwardRef(({ onClose, errorRows }, ref) => {
  const { t } = useTranslation()
  const [isShowModal, setIsShowModal] = useState<boolean>(false)
  const [api, contextHolder] = notification.useNotification()
  const getHeaderName = useCallback(
    (field: string): string => {
      return t(`ACHExceptionRule:uploadModal.uploadFile.errorDetails.tableCol.${field}`)
    },
    [t],
  )
  const columnDefs: TableColumnDefs[] = useMemo(
    (): TableColumnDefs[] => [
      {
        colId: 'importType',
        field: 'importType',
        headerName: getHeaderName('importType'),
        hide: false,
        defaultSelect: true,
        width: 120,
        minWidth: 120,
        maxWidth: 120,
      },
      {
        colId: 'fileName',
        field: 'fileName',
        headerName: getHeaderName('fileName'),
        hide: false,
        defaultSelect: true,
        width: 190,
        minWidth: 190,
        maxWidth: 190,
      },
      {
        colId: 'inputDate',
        field: 'inputDate',
        headerName: getHeaderName('inputDate'),
        hide: false,
        defaultSelect: true,
        cellRenderer: (data: any) => {
          return (
            <Typography.Text
              style={{
                fontSize: 14,
                fontFamily: 'MyriadPro',
              }}
            >
              {data.data.inputDate}
            </Typography.Text>
          )
        },
        width: 140,
        minWidth: 140,
        maxWidth: 140,
      },
      {
        colId: 'status',
        field: 'status',
        headerName: getHeaderName('status'),
        hide: false,
        defaultSelect: true,
        cellRenderer: (data: any) => {
          return <StatusTag status={data.data.status} />
        },
        width: 152,
        minWidth: 152,
        maxWidth: 152,
      },
      {
        colId: 'processedItems',
        field: 'processedItems',
        headerName: getHeaderName('processedItems'),
        hide: false,
        defaultSelect: true,
        width: 96,
        minWidth: 96,
        maxWidth: 96,
      },
      {
        colId: 'rejectedItems',
        field: 'rejectedItems',
        headerName: getHeaderName('rejectedItems'),
        hide: false,
        defaultSelect: true,
        width: 96,
        minWidth: 96,
        maxWidth: 96,
      },
      {
        colId: 'uploadedBy',
        field: 'uploadedBy',
        headerName: getHeaderName('uploadedBy'),
        hide: false,
        defaultSelect: true,
        width: 100,
        minWidth: 100,
        maxWidth: 100,
      },
    ],
    [getHeaderName],
  )

  const showExporting = useCallback(() => {
    api.info({
      message: t('common:exporting'),
      placement: 'topRight',
      duration: 3000,
      icon: (
        <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} size='small' style={{ fontSize: '20px' }} />
      ),
      style: {
        backgroundColor: '#F5FBFF',
        borderRadius: '8px',
        padding: '12px 16px',
        border: '1px solid #43BBFF',
        boxShadow: 'none',
        height: '50px',
        width: '250px',
        display: 'flex',
        alignItems: 'stretch',
      },
    })
  }, [api, t])

  const showExportingError = useCallback(
    (error: any) => {
      api.error({
        message: t(`SystemMessages:${SYSTEM_MESSAGE_IDS.GENERAL_ERROR_MESSAGE}`),
        placement: 'topRight',
        duration: 3,
        closeIcon: <></>,
      })
    },
    [api, t],
  )
  const rowData = useMemo(() => {
    return [
      {
        importType: errorRows.importType ?? '',
        fileName: errorRows?.fileName,
        inputDate: errorRows?.inputDate,
        status: errorRows?.status,
        processedItems: errorRows?.processedCount ?? 0,
        rejectedItems: errorRows?.rejectedCount ?? 0,
        uploadedBy: errorRows?.uploadedBy,
      },
    ]
  }, [errorRows])

  const handleDownload = useCallback(async () => {
    try {
      showExporting()
      const data = await (await fetch(errorRows?.uploadedPath ?? '')).arrayBuffer()
      const dataBlob = new Blob([new Uint8Array(data)])
      saveAs(dataBlob, errorRows?.fileName)
      if (data) {
        api.destroy()
      }
    } catch (error) {
      // Follow show download toast message logic in common table
      api.destroy()
      setTimeout(() => {
        showExportingError(error)
      }, 500)
    }
  }, [api, showExporting, showExportingError, errorRows])

  const handleCloseModal = () => {
    setIsShowModal(false)
    onClose && onClose()
  }

  const handleOpenModal = () => {
    setIsShowModal(true)
  }

  useImperativeHandle(
    ref,
    () => {
      return {
        openModal() {
          handleOpenModal()
        },
        closeModal() {
          handleCloseModal()
        },
      }
    },
    [],
  )

  return (
    <CommonModal
      title={t('ACHExceptionRule:uploadModal.uploadFile.errorDetails.errorDetails')}
      open={isShowModal}
      cancelText={t('ACHExceptionRule:uploadModal.uploadFile.errorDetails.close')}
      cancelButtonProps={{ style: { width: 102, fontWeight: 600 }, size: 'large' }}
      onCancel={handleCloseModal}
      onClose={handleCloseModal}
      okButtonProps={{
        style: { display: 'none' },
      }}
      width={960}
    >
      {contextHolder}
      <Flex style={{ width: '100%', justifyContent: 'flex-end' }}>
        <Flex
          justify='center'
          align='center'
          style={{
            border: `1px solid ${colorBorder}`,
            height: 32,
            padding: '4px 12px',
            borderRadius: 6,
            cursor: 'pointer',
            gap: 4,
          }}
          onClick={handleDownload}
        >
          <DownloadIcon />
          <Typography.Text style={{ fontSize: 14, fontWeight: 700 }}>
            {t('ACHExceptionRule:uploadModal.uploadFile.errorDetails.downloadFile')}
          </Typography.Text>
        </Flex>
      </Flex>
      <div className={'error-details-upload-account'} style={{ width: '100%' }}>
        <CommonTable
          columnDefs={columnDefs}
          rowData={rowData}
          disableTableControl
          displayActionColumn={false}
          handleChooseColumn={async () => {}}
          handleSearch={async () => {}}
          autoSort={false}
          getHeaderName={null}
        />
      </div>

      <Flex justify={'space-between'} align={'center'} style={{ height: 32, marginBottom: 12 }}>
        <Typography.Text style={{ fontSize: 14, fontWeight: 700 }}>
          {t('ACHExceptionRule:uploadModal.uploadFile.errorDetails.errorMessages')}
        </Typography.Text>
      </Flex>

      <Space size={8} direction={'vertical'} style={{ width: '100%', height: 265, overflow: 'scroll' }}>
        {errorRows?.rejectedItems?.map((error: any, index: number) => {
          return (
            <Row
              key={index}
              style={{
                border: `1px solid ${colorBgErrorBold}`,
                width: '100%',
                padding: '8px 12px',
                borderRadius: 12,
                backgroundColor: colorBgError,
              }}
            >
              <Typography.Text style={{ fontSize: 14 }}>{getErrorMessage(error, t)}</Typography.Text>
            </Row>
          )
        })}
      </Space>
    </CommonModal>
  )
})

export default ErrorDetailsUpload
